import React, { useCallback, useEffect, useState } from 'react'
import { makeStyles } from '@material-ui/styles'
import Text from 'core/elements/Text'
import SubmitButton from 'core/components/buttons/SubmitButton'
import { FormFieldCard } from 'core/components/validatedForm/FormFieldCard'
import { IconInfo } from 'core/components/validatedForm/Info'
import Theme from 'core/themes/model'
import AzureCloudProviderRequirementDialog from '../azure/AzureCloudProviderRequirementDialog'
import { CloudProviders } from 'app/plugins/infrastructure/components/cloudProviders/model'
import { routes } from 'core/utils/routes'
import BulletList from 'core/components/BulletList'
import { ErrorMessage } from 'core/components/validatedForm/ErrorMessage'
import useScopedPreferences from 'core/session/useScopedPreferences'
import { isServiceTenant } from 'k8s/util/helpers'
import { userTenantsSelector } from 'account/components/userManagement/tenants/selectors'
import { useSelector } from 'react-redux'
import useListAction from 'core/hooks/useListAction'
import { listCloudProviders } from 'app/plugins/infrastructure/components/cloudProviders/new-actions'
import { cloudProvidersSelector } from 'app/plugins/infrastructure/components/cloudProviders/selectors'

const useStyles = makeStyles<Theme>((theme) => ({
  requirements: {
    display: 'flex',
    flexFlow: 'row nowrap',
    justifyContent: 'space-between',
    margin: theme.spacing(2, 4, 1, 4),
  },
  text: {
    marginTop: theme.spacing(0.5),
    marginLeft: theme.spacing(3),
  },
  bulletList: {
    marginLeft: theme.spacing(2),
    flex: 1,
  },
  actionRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: theme.spacing(2),
  },
  formCard: {
    color: theme.palette.grey[700],
  },
}))

const AzureReqs = [
  'The AKS Cluster must be accessible via a Public IP',
  'AKS Cluster using Private Virtual Networks can be added in Read-Only mode',
  'The service principal that was used to create the Cloud Provider must have access to the AKS Cluster as an Azure Kubernetes Service Cluster Admin Role',
]

const ImportAKSRequirements = ({ onComplete, platform }) => {
  const classes = useStyles({})
  const [showDialog, setShowDialog] = useState(false)
  const [errorMessage, setErrorMessage] = useState(null)
  const { loading } = useListAction(listCloudProviders)
  const cloudProviders = useSelector(cloudProvidersSelector)
  const userTenants = useSelector(userTenantsSelector)
  const { prefs } = useScopedPreferences()
  const { currentTenant } = prefs

  useEffect(() => {
    setErrorMessage(null)
  }, [currentTenant])

  const handleClick = useCallback(() => {
    if (!isServiceTenant(userTenants, currentTenant)) {
      setErrorMessage('Only authorized for service tenant')
      return
    }
    const hasAzureProvider = !!cloudProviders.find(
      (provider) => provider.type === CloudProviders.Azure,
    )
    if (!hasAzureProvider) {
      setShowDialog(true)
    } else {
      onComplete(routes.cluster.import.aks.path())
    }
  }, [onComplete, cloudProviders, userTenants, currentTenant])

  return (
    <>
      {showDialog && (
        <AzureCloudProviderRequirementDialog
          showDialog={showDialog}
          setShowDialog={setShowDialog}
        />
      )}
      <FormFieldCard
        className={classes.formCard}
        title="Microsoft AKS Cluster Management"
        // link={
        //   <ExternalLink textVariant="caption2" url={aksHelpLink}>
        //     AKS Help
        //   </ExternalLink>
        // }
      >
        <Text variant="body2" className={classes.text}>
          Platform9 is able to connect to Azure and import existing AKS clusters to bring them under
          management. Once connected to Platform9 you will be able to deploy Platform9 Monitoring,
          leverage the Application Catalog to deploy apps and configure RBAC, Pods, Deployments and
          Services.
        </Text>
        <IconInfo
          icon="info-circle"
          title="The following requirements must be met to be able to import and manage AKS Clusters:"
        >
          <div className={classes.requirements}>
            <BulletList className={classes.bulletList} items={AzureReqs} />
          </div>
        </IconInfo>
        <div className={classes.actionRow}>
          <SubmitButton onClick={handleClick}>Import AKS Clusters</SubmitButton>
        </div>
        <ErrorMessage>{errorMessage}</ErrorMessage>
      </FormFieldCard>
    </>
  )
}
export default ImportAKSRequirements
